import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { Card } from "@/projects/components/ui/card";
import { Button } from "@/projects/components/ui/button";
import { Textarea } from "@/projects/components/ui/textarea";
import { Label } from "@/projects/components/ui/label";
import {
  ArrowLeft,
  Trophy,
  AlertTriangle,
  XCircle,
  FileVideo,
  Loader2,
} from "lucide-react";
import { cn } from "@/projects/lib/utils";
import { useEffect, useState } from "react";
import { toast } from "@/projects/lib/toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/projects/components/ui/alert-dialog";
import { StatusBadge } from "@/projects/components/status-badge";
import { AiFeedbackAssistant } from "@/projects/teacher/components/ai-feedback-assistant";
import type { Submission } from "@/projects/lib/store";
import {
  approveStudentVideoSubmission,
  fetchStudentVideoSubmission,
  getSubmissionApiErrorMessage,
  rejectStudentVideoSubmission,
} from "@/projects/lib/submissionsApi";

export function AdminVerifyPage() {
  const { id } = useParams({ from: "/admin/verify/$id" });
  const navigate = useNavigate();
  const [s, setS] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [choice, setChoice] = useState<
    "Not Proper" | "Not Eligible" | "Ready for Reward" | null
  >(null);
  const [remarks, setRemarks] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const row = await fetchStudentVideoSubmission(id);
        if (cancelled) {
          return;
        }
        setS(row);
        setRemarks(row.teacherRemarks ?? "");
        if (row.status === "Not Proper" || row.status === "Ready for Reward" || row.status === "Not Eligible") {
          setChoice(row.status as "Not Proper" | "Not Eligible" | "Ready for Reward");
        } else {
          setChoice(null);
        }
      } catch {
        if (!cancelled) {
          setNotFound(true);
          setS(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const doSave = async () => {
    if (!s || !choice) {
      toast.error("Please choose a verification status.");
      return;
    }
    if ((choice === "Not Proper" || choice === "Not Eligible") && !remarks.trim()) {
      toast.error(`Please mention the reason why the video is ${choice === "Not Proper" ? "not proper" : "not eligible"}.`);
      return;
    }

    const apiId = s.apiId || id;
    setSaving(true);
    try {
      if (choice === "Ready for Reward") {
        await approveStudentVideoSubmission(apiId, remarks.trim());
      } else {
        await rejectStudentVideoSubmission(
          apiId,
          remarks.trim(),
          choice === "Not Proper" ? "not_proper" : "not_eligible"
        );
      }
      toast.success("Verification saved successfully");
      navigate({ to: "/admin/submissions" });
    } catch (error) {
      toast.error(
        getSubmissionApiErrorMessage(error, "Failed to save verification.")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    if (!choice) {
      toast.error("Please choose a verification status.");
      return;
    }
    if ((choice === "Not Proper" || choice === "Not Eligible") && !remarks.trim()) {
      toast.error(`Please mention the reason why the video is ${choice === "Not Proper" ? "not proper" : "not eligible"}.`);
      return;
    }
    if (choice === "Ready for Reward") {
      setConfirmOpen(true);
    } else {
      void doSave();
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="admin" title="Verify Submission">
        <div className="text-muted-foreground flex items-center justify-center gap-2 py-24 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading submission…
        </div>
      </DashboardLayout>
    );
  }

  if (notFound || !s) {
    return (
      <DashboardLayout role="admin" title="Not found">
        <div className="bg-card rounded-xl border p-8 text-center">
          <div className="text-lg font-semibold">Submission not found</div>
          <Link to="/admin/submissions">
            <Button variant="outline" className="mt-4">
              Back
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      role="admin"
      title="Verify Submission"
      breadcrumb={["Home", "Submissions", s.id]}
    >
      <Link
        to="/admin/submissions"
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Video Submissions
      </Link>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="aspect-video overflow-hidden rounded-lg bg-black">
            {s.videoUrl ? (
              <video
                key={s.videoUrl}
                controls
                className="h-full w-full"
                src={s.videoUrl}
              />
            ) : (
              <div className="text-muted-foreground grid h-full place-items-center text-sm">
                No video URL available for this submission.
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="bg-royal-soft text-royal grid h-9 w-9 place-items-center rounded-lg">
                <FileVideo className="h-4 w-4" />
              </div>
              <div>
                <div className="font-medium">{s.fileName}</div>
                <div className="text-muted-foreground text-xs">
                  {s.fileSizeMB} MB
                </div>
              </div>
            </div>
            <StatusBadge status={s.status} />
          </div>

          <div className="mt-6">
            <div className="mb-2 text-sm font-semibold">
              Verification Status
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <StatusChoice
                selected={choice === "Ready for Reward"}
                color="success"
                icon={Trophy}
                title="Ready for Reward"
                desc="Video meets criteria and deserves recognition."
                onClick={() => setChoice("Ready for Reward")}
              />
              <StatusChoice
                selected={choice === "Not Proper"}
                color="destructive"
                icon={AlertTriangle}
                title="Not Proper"
                desc="Video does not meet the required standard."
                onClick={() => setChoice("Not Proper")}
              />
              <StatusChoice
                selected={choice === "Not Eligible"}
                color="destructive"
                icon={XCircle}
                title="Not Eligible"
                desc="Student is not eligible for this submission."
                onClick={() => setChoice("Not Eligible")}
              />
            </div>

            <div className="mt-4">
              <AiFeedbackAssistant
                studentName={s.studentName}
                videoTitle={s.videoTitle}
                decision={choice}
                notes={remarks}
                videoUrl={s.videoUrl}
                onApply={t => setRemarks(t)}
              />
            </div>

            <div className="mt-4">
              <Label>
                Remarks{" "}
                {(choice === "Not Proper" || choice === "Not Eligible") && (
                  <span className="text-destructive">*</span>
                )}
              </Label>
              <Textarea
                value={remarks}
                onChange={e => setRemarks(e.target.value)}
                placeholder={
                  choice === "Not Proper"
                    ? "Mention the reason why the video is not proper"
                    : choice === "Not Eligible"
                      ? "Mention the reason why the student is not eligible"
                      : "Optional remarks…"
                }
                rows={4}
                disabled={saving}
              />
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Link to="/admin/submissions">
                <Button variant="outline" disabled={saving}>
                  Cancel
                </Button>
              </Link>
              <Button onClick={handleSubmit} disabled={saving || !choice}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Verification
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="text-sm font-semibold">Submission Details</div>
          <div className="mt-3 space-y-2 text-sm">
            <Row
              k="Reference"
              v={<span className="text-royal font-mono">{s.id}</span>}
            />
            <Row k="Student" v={s.studentName} />
            <Row k="Student ID" v={s.studentId} />
            <Row k="Title" v={s.videoTitle} />
            <Row k="Submitted" v={new Date(s.submittedAt).toLocaleString()} />
            <Row k="Assigned Teacher" v={s.assignedTeacherName ?? "—"} />
            <Row
              k="Assigned"
              v={s.assignedAt ? new Date(s.assignedAt).toLocaleString() : "—"}
            />
          </div>
        </Card>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Ready for Reward?</AlertDialogTitle>
            <AlertDialogDescription>
              This will approve the submission and update its status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              onClick={e => {
                e.preventDefault();
                setConfirmOpen(false);
                void doSave();
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function StatusChoice({
  selected,
  color,
  icon: Icon,
  title,
  desc,
  onClick,
}: {
  selected: boolean;
  color: "success" | "destructive";
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-all",
        selected
          ? color === "success"
            ? "border-success bg-success/5"
            : "border-destructive bg-destructive/5"
          : "border-border bg-card hover:bg-muted/50"
      )}
    >
      <div
        className={cn(
          "grid h-10 w-10 shrink-0 place-items-center rounded-lg",
          color === "success"
            ? "bg-success/10 text-success"
            : "bg-destructive/10 text-destructive"
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-muted-foreground text-xs">{desc}</div>
      </div>
    </button>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-right font-medium">{v}</span>
    </div>
  );
}
